// VendorPicker — "which supplier?", asked once for the whole app.
//
// The PO form grew this first: fetch GET /procurement/vendors, turn the rows
// into ComboboxOption (code as the chip, the type's label as the group, an
// effective hold as the sublabel) and hand them to EntityCombobox. The quote
// lane then needed the same question and had nothing to call, so it offered a
// create checkbox and a sentence naming suppliers it would not let anybody
// pick. That is what this file removes: one fetch, one option shape, one hold
// predicate, and every surface that asks gets the same list.
//
// ── IT FETCHES FOR ITSELF ────────────────────────────────────────────────────
// Deliberate, and the opposite of useCodedVendors, which takes the api in and is
// called ONCE per screen because a scanned PO is thirty cards that might each
// hold a SkuBuilder. A vendor picker is one per page: a header has one vendor,
// a quote has one supplier. Owning the fetch is what lets a page mount it and
// be done, and if a screen ever wants two of these the answer is to lift the
// list, not to make every caller carry it today.
//
// ── WHY `onVendorsLoaded` ────────────────────────────────────────────────────
// One caller has a NAME and needs an id: the reorder handoff writes
// `vendor_name` into sessionStorage and the PO form resolves it against the
// list. That resolution cannot happen before the list lands, so the picker says
// when it has. It is not a general "give me your state" hatch — anything that
// wants to know which vendor is chosen already gets the whole row on onSelect.
// It reports the list the field OFFERS, `extraVendors` included, and fires
// again whenever that list changes, because a caller resolving against it needs
// the same set the person can see.
//
// ── WHY `extraVendors` ───────────────────────────────────────────────────────
// The quote and scan lanes create a vendor from a board line card's drawer
// while this field is mounted. The row is in the database, this component's one
// fetch already happened, and refetching per page would be the page holding the
// list again. So the caller hands its just-created rows in and they merge: the
// fetched row wins on a collision, and an addition slots into its own type's
// run so the group headers stay one per type. A reviewer who creates the
// document's own supplier from a card can then pick it without a reload.
//
// ── THE TYPE LABELS ARE THE API'S ────────────────────────────────────────────
// `types` ships with the vendors, active and in order, because an admin edits
// that taxonomy at /admin/vendor-types. A key the payload does not carry (a
// retired type still attached to an old vendor) falls back to `formatType`,
// which title-cases the key rather than hiding the vendor. No copy of those
// strings lives here. See docs/features/shared-combobox.md.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from './Toast/ToastContext';
import EntityCombobox, { type ComboboxOption } from './EntityCombobox';
import { formatType, type VendorTypeOption } from '../Procurement/vendorTypeLabels';

/** A vendor row as GET /procurement/vendors returns it. */
export interface VendorPickerVendor {
  id: string;
  code: string | null;
  name: string;
  type: string;
  on_hold?: boolean;
  release_date?: string | null;
  is_disabled?: boolean;
  notes?: string;
}

interface VendorsResponse {
  vendors: VendorPickerVendor[];
  types: VendorTypeOption[];
}

export interface VendorPickerProps {
  /** The chosen vendor's id, or null / '' for none. */
  value: string | null;
  /** The whole row, so a caller never has to look one up again. */
  onSelect: (vendor: VendorPickerVendor | null) => void;
  label?: string;
  id?: string;
  placeholder?: string;
  openOnFocus?: boolean;
  disabled?: boolean;
  inputClassName?: string;
  labelClassName?: string;
  className?: string;
  emptyMessage?: React.ReactNode;
  noOptionsMessage?: React.ReactNode;
  /**
   * Fires once the list has loaded, with every vendor the field offers
   * (`extraVendors` merged in), and again whenever that list changes. For the
   * caller that has a NAME to resolve to an id (the reorder prefill), and for
   * the one that keeps a set of ids it considers live (the quote lane's
   * retired-supplier check).
   */
  onVendorsLoaded?: (vendors: VendorPickerVendor[]) => void;
  /**
   * Rows that exist in the database but landed after this component fetched —
   * a vendor created from a drawer on the same screen. Merged into the options
   * and into `onVendorsLoaded`; a fetched row with the same id wins.
   */
  extraVendors?: VendorPickerVendor[];
}

/**
 * An on_hold vendor whose release date has already passed is not on hold any
 * more. Exported so the flag in the list and the warning a page draws under the
 * field can never disagree: one predicate, both readers.
 */
export function onHoldNow(v: VendorPickerVendor): boolean {
  if (!v.on_hold) return false;
  return !(v.release_date && v.release_date <= new Date().toISOString().slice(0, 10));
}

/**
 * Put one addition where the API would have sent it: inside its own type's run,
 * by name. Appending at the end instead would draw a SECOND group header for a
 * type already on the list, which reads as two board-house sections.
 */
function insertByTypeThenName(
  list: VendorPickerVendor[],
  row: VendorPickerVendor
): VendorPickerVendor[] {
  const runStart = list.findIndex((v) => v.type === row.type);
  if (runStart === -1) return [...list, row];
  let at = runStart;
  while (at < list.length && list[at].type === row.type && list[at].name.localeCompare(row.name) <= 0) {
    at += 1;
  }
  return [...list.slice(0, at), row, ...list.slice(at)];
}

const VendorPicker: React.FC<VendorPickerProps> = ({
  value,
  onSelect,
  label = 'Vendor',
  id,
  placeholder = 'Type to search',
  openOnFocus,
  disabled,
  inputClassName,
  labelClassName,
  className,
  emptyMessage = 'No vendor matches that.',
  noOptionsMessage = 'No vendors yet. An admin adds them under Admin, Vendors.',
  onVendorsLoaded,
  extraVendors,
}) => {
  const api = useApi();
  const toast = useToast();

  // null until the fetch lands, so nothing reports an empty list as the answer:
  // a caller that keeps a set of live ids would read every vendor as retired.
  const [fetched, setFetched] = useState<VendorPickerVendor[] | null>(null);
  const [types, setTypes] = useState<VendorTypeOption[]>([]);

  // Held in a ref so a caller that passes an inline arrow (all of them) does
  // not re-run the fetch on every render of its page.
  const loadedRef = useRef(onVendorsLoaded);
  loadedRef.current = onVendorsLoaded;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = (await api.get('/procurement/vendors')) as VendorsResponse;
        if (cancelled) return;
        setFetched(data.vendors || []);
        setTypes(data.types || []);
      } catch (err: any) {
        if (cancelled) return;
        toast.error(err?.data?.error?.message || err?.message || 'Failed to load vendor list');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // What the field offers: the fetch, plus rows the caller made since it.
  const vendors = useMemo<VendorPickerVendor[]>(() => {
    const rows = fetched || [];
    if (!extraVendors?.length) return rows;
    return extraVendors.reduce(
      (list, extra) => (list.some((v) => v.id === extra.id) ? list : insertByTypeThenName(list, extra)),
      rows
    );
  }, [fetched, extraVendors]);

  // Announced after the fetch only, and again whenever the offered SET changes.
  // Before the fetch there is no answer, and an empty one is a wrong answer.
  // Compared by id rather than by array identity: a caller is free to build
  // `extraVendors` inline, and re-reporting on identity alone would then loop
  // against a callback that sets state (the quote lane's does).
  const reportedIdsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!fetched) return;
    const ids = vendors.map((v) => v.id).join(',');
    if (reportedIdsRef.current === ids) return;
    reportedIdsRef.current = ids;
    loadedRef.current?.(vendors.filter((v) => !v.is_disabled));
  }, [fetched, vendors]);

  const typeLabel = useCallback(
    (key: string) => types.find((t) => t.key === key)?.label || formatType(key),
    [types]
  );

  // One option per vendor, in the order the API sent them (type, then name), so
  // the group headers fall out of that order instead of being sorted here. The
  // endpoint leaves disabled vendors out unless asked, so nothing retired
  // arrives from the fetch; an `extraVendors` row is the caller's, and says
  // `Retired` rather than passing itself off as live.
  const options = useMemo<ComboboxOption[]>(
    () =>
      vendors.map((v) => ({
        id: v.id,
        label: v.name,
        // The two-letter code is also the vendor segment of a board SKU, and
        // typing it finds the vendor.
        chip: v.code,
        group: typeLabel(v.type),
        // Selectable, as it has always been: a hold blocks submit, not
        // drafting. Saying so here means it is read before picking, not after.
        sublabel: v.is_disabled ? 'Retired' : onHoldNow(v) ? 'On hold' : null,
      })),
    [vendors, typeLabel]
  );

  return (
    <EntityCombobox
      id={id}
      label={label}
      value={value}
      options={options}
      onSelect={(o) => onSelect(o ? vendors.find((v) => v.id === String(o.id)) || null : null)}
      placeholder={placeholder}
      openOnFocus={openOnFocus}
      disabled={disabled}
      className={className}
      inputClassName={inputClassName}
      labelClassName={labelClassName}
      emptyMessage={emptyMessage}
      noOptionsMessage={noOptionsMessage}
    />
  );
};

export default VendorPicker;
